import { useState } from "react";
import { FormProvider } from "react-hook-form";

import { SVGSprite } from "@/components/Atoms";
import FormInputs from "@/components/Organisms/HookFormInputs/V1";
import Typography from "@/foundation/Typography/V2";

import useBookYogaNutritionTrialForm from "./useBookYogaNutritionTrialForm";

import { classNameGenerator, clsx } from "@/utils";

import { useAppSelector } from "@/hooks";

import styles from "./BookYogaNutritionTrialForm.module.scss";

interface BookYogaNutritionTrialFormProps {
  showModal: any;
  standalone?: boolean;
}

const BookYogaNutritionTrialForm: React.FC<BookYogaNutritionTrialFormProps> = ({
  showModal,
  standalone,
}) => {
  const { packageData } = showModal;

  const cls = classNameGenerator(styles);

  const { data } = useAppSelector((state) => state.bootCamp);

  const [selectedPackData, setSelectedPackData] = useState(
    packageData?.[0] || {}
  );
  const [formError, setFormError] = useState("");

  const { methods, isLoading, onBlur, packPurchaseHandler } =
    useBookYogaNutritionTrialForm({ packData: selectedPackData, setFormError });

  const { imageUrl, label, prefix } = selectedPackData?.formSection || {};

  const components = [
    {
      name: "name",
      props: {
        name: "name",
        className: cls("name"),
      },
    },
    {
      name: "email",
      props: {
        name: "email",
        className: cls("email"),
        onBlur,
      },
    },
    {
      name: "phone",
      props: {
        name: "mobile",
        className: cls("phone"),
        onBlur,
      },
    },
    {
      name: "submit",
      props: {
        children: (
          <Typography
            variant="span"
            dangerouslySetInnerHTML={{
              __html: `Confirm & Pay ${selectedPackData?.currencySymbol}&nbsp;${selectedPackData?.price}`,
            }}
          />
        ),
        className: cls("submitButton"),
        isLoading,
      },
    },
    {
      props: {
        children: (
          <Typography
            variant="paragraph1"
            className={cls("infoText")}
            dangerouslySetInnerHTML={{
              __html:
                "Once enrolled, download the Shyft app to attend or reschedule your yoga <br/>classes or consultations",
            }}
          />
        ),
      },
    },
    {
      name: "error",
      props: {
        error: formError,
        className: cls("formError"),
      },
    },
  ];

  return (
    <div
      className={clsx(
        cls("bookYogaNutritionTrialForm"),
        standalone ? cls("standalone") : ""
      )}
    >
      <div className={cls("bookYogaNutritionTrialFormWrapper")}>
        <Typography
          variant="paragraph1"
          className={cls("prefixHeading")}
          dangerouslySetInnerHTML={{ __html: prefix }}
        />
        {imageUrl ? (
          <div className={cls("nutritionistImageContainer")}>
            <img
              src={imageUrl}
              alt="nutritionist image"
              className={cls("nutritionistImage")}
            />
            <div className={cls("heading")}>
              <Typography
                variant="paragraph1"
                className={cls("prefix")}
                dangerouslySetInnerHTML={{ __html: prefix }}
              />
              <Typography
                variant="paragraph1"
                className={cls("priceLabel")}
                dangerouslySetInnerHTML={{ __html: label }}
              />
            </div>
          </div>
        ) : (
          <div className={cls("header")}>
            <Typography
              variant="paragraph1"
              className={cls("prefix")}
              dangerouslySetInnerHTML={{ __html: prefix }}
            />
            <Typography
              variant="heading2"
              className={cls("title")}
              dangerouslySetInnerHTML={{ __html: label }}
            />
          </div>
        )}
        <FormProvider {...methods}>
          <div className={cls("trialPacks")}>
            {packageData?.map((trialPack, index) => {
              const isActive = trialPack?.title === selectedPackData?.title;

              return (
                <div
                  key={index}
                  className={clsx(
                    cls("trialPack"),
                    isActive ? cls("active") : ""
                  )}
                  onClick={() => setSelectedPackData(trialPack)}
                >
                  <SVGSprite
                    icon={
                      isActive
                        ? "icon-trial-pack-radio-active"
                        : "icon-trial-pack-radio"
                    }
                    className={cls("icon")}
                  />
                  {trialPack?.currencySymbol && trialPack?.price !== null && <Typography
                    variant="paragraph1"
                    className={cls("title")}
                    dangerouslySetInnerHTML={{
                      __html: `${trialPack?.title} <strong>${trialPack?.currencySymbol}&nbsp;${trialPack?.price}<strong>`,
                    }}
                  />}
                </div>
              );
            })}
          </div>
          <form className={cls("formWrapper")} onSubmit={packPurchaseHandler}>
            <FormInputs components={components} />
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default BookYogaNutritionTrialForm;
